import type { DocumentSnapshot } from "firebase/firestore";
import type { User } from "firebase/auth";
import {
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  Timestamp,
  updateDoc,
} from "firebase/firestore";
import { firestore } from "./firebaseClient";

const ORGANIZATIONS_COLLECTION = "organizations";
const USERS_COLLECTION = "users";
const DEFAULT_PAGE_SIZE = 20;
const MAX_USERS = 2000;

export type SubscriptionStatus =
  | "trialing"
  | "active"
  | "past_due"
  | "canceled"
  | "unpaid"
  | "incomplete"
  | "incomplete_expired"
  | "paused"
  | "pending_ves"
  | "ves_rejected";

export type PlanId =
  | "micro"
  | "standard"
  | "professional"
  | "enterprise"
  | "custom";

export interface PlanLimits {
  id?: PlanId | string;
  name: string;
  monthlyPrice?: number | null;
  currency?: string;
  billingType?: "fixed" | "quote";
  marketingFeatures?: string[];
  highlighted?: boolean;
  stripeProductId?: string | null;
  stripePriceId?: string | null;
  maxSalesPeople: number;
  maxCustomers: number;
  maxInventoryItems: number;
  maxSuppliers: number;
  maxAdmins: number;
}

export interface OrganizationRecord {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  active: boolean;
  subscriptionStatus: SubscriptionStatus | null;
  planId: PlanId | null;
  plan: PlanLimits | null;
  currentPeriodEnd: Date | null;
  paymentMethod?: "ves" | "stripe" | "binance";
  paymentMethodsAllowed?: Array<"ves" | "stripe" | "binance">;
  /** When set, only these plan IDs are shown in the Paywall selector (e.g. when trialing). Empty/undefined = all plans. */
  selectablePlanIds?: string[] | null;
  hasUsedFreeTrial?: boolean;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  stripePriceId?: string;
  intelligenceCapacityId?: "lite" | "plus" | "extra" | null;
  intelligencePrice?: number | null;
  intelligenceCreditsAllowance?: number | null;
  intelligenceCreditsUsed?: number | null;
  intelligenceTrialing?: boolean;
  intelligencePeriodEnd?: Date | null;
  hasUsedIntelligenceTrial?: boolean;
  created: Date | null;
  updated: Date | null;
}

const SUBSCRIPTION_STATUSES: SubscriptionStatus[] = [
  "trialing",
  "active",
  "past_due",
  "canceled",
  "unpaid",
  "incomplete",
  "incomplete_expired",
  "paused",
  "pending_ves",
  "ves_rejected",
];


const mapTimestamp = (value: unknown): Date | null => {
  if (value && typeof value === "object" && "toDate" in value) {
    return (value as { toDate: () => Date }).toDate();
  }
  if (value && typeof value === "object" && "seconds" in value) {
    const s = (value as { seconds: number }).seconds;
    return new Date(s * 1000);
  }
  return null;
};

/** Safe number from plan (legacy orgs may lack limits). */
function planNum(o: Record<string, unknown>, key: string): number {
  const v = o[key];
  return typeof v === "number" && Number.isFinite(v) ? v : 0;
}

const mapPlan = (value: unknown): PlanLimits | null => {
  if (!value || typeof value !== "object") return null;
  const o = value as Record<string, unknown>;
  // Require at least a name or id so we can display something; allow missing/partial limits for legacy orgs.
  const hasName = typeof o.name === "string" && o.name.trim().length > 0;
  const hasId = typeof o.id === "string" && o.id.trim().length > 0;
  if (!hasName && !hasId) return null;
  return {
    id:
      typeof o.id === "string"
        ? (o.id as PlanId | string)
        : undefined,
    name: typeof o.name === "string" ? o.name : (typeof o.id === "string" ? String(o.id) : "Plan"),
    monthlyPrice:
      typeof o.monthlyPrice === "number"
        ? o.monthlyPrice
        : o.monthlyPrice === null
          ? null
          : undefined,
    currency: typeof o.currency === "string" ? o.currency : undefined,
    billingType:
      o.billingType === "fixed" || o.billingType === "quote"
        ? o.billingType
        : undefined,
    marketingFeatures: Array.isArray(o.marketingFeatures)
      ? o.marketingFeatures.filter((v): v is string => typeof v === "string")
      : undefined,
    highlighted: typeof o.highlighted === "boolean" ? o.highlighted : undefined,
    stripeProductId:
      typeof o.stripeProductId === "string" ? o.stripeProductId : undefined,
    stripePriceId:
      typeof o.stripePriceId === "string" ? o.stripePriceId : undefined,
    maxSalesPeople: planNum(o, "maxSalesPeople"),
    maxCustomers: planNum(o, "maxCustomers"),
    maxInventoryItems: planNum(o, "maxInventoryItems"),
    maxSuppliers: planNum(o, "maxSuppliers"),
    maxAdmins: planNum(o, "maxAdmins"),
  };
};

function mapRecord(
  id: string,
  data: Record<string, unknown>
): OrganizationRecord {
  const billing = (data.billing as Record<string, unknown> | undefined) ?? {};
  const status = billing.subscriptionStatus as string | undefined;
  const planId = billing.planId as string | undefined;
  const plan = mapPlan(data.plan);
  const inferredPlanId = typeof plan?.id === "string" ? plan.id : undefined;
  const sourcePlanIdRaw = planId ?? inferredPlanId;
  const sourcePlanId = sourcePlanIdRaw === "basic" ? "micro" : sourcePlanIdRaw;
  const validStatus = SUBSCRIPTION_STATUSES.includes(
    status as SubscriptionStatus
  )
    ? (status as SubscriptionStatus)
    : null;
  const validPlanId =
    sourcePlanId === "micro" ||
    sourcePlanId === "standard" ||
    sourcePlanId === "professional" ||
    sourcePlanId === "enterprise"
      ? (sourcePlanId as PlanId)
      : null;

  const intel =
    billing.intelligence && typeof billing.intelligence === "object"
      ? (billing.intelligence as Record<string, unknown>)
      : {};
  const rawIntelCapacity =
    typeof intel.capacityId === "string" ? intel.capacityId.trim().toLowerCase() : "";
  const intelCapacity =
    rawIntelCapacity === "lite" ||
    rawIntelCapacity === "plus" ||
    rawIntelCapacity === "extra"
      ? rawIntelCapacity
      : rawIntelCapacity === "pro"
        ? "extra"
        : null;

  return {
    id,
    name: typeof data.name === "string" ? data.name : "",
    email: typeof data.email === "string" ? data.email : undefined,
    phone: typeof data.phone === "string" ? data.phone : undefined,
    active: data.active !== false,
    subscriptionStatus: validStatus,
    planId: validPlanId,
    plan,
    currentPeriodEnd: mapTimestamp(billing.currentPeriodEnd),
    paymentMethod:
      billing.paymentMethod === "ves" ||
      billing.paymentMethod === "stripe" ||
      billing.paymentMethod === "binance"
        ? billing.paymentMethod
        : undefined,
    paymentMethodsAllowed: Array.isArray(billing.paymentMethodsAllowed)
      ? (billing.paymentMethodsAllowed.filter(
          (m): m is "ves" | "stripe" | "binance" =>
            m === "ves" || m === "stripe" || m === "binance"
        ) as Array<"ves" | "stripe" | "binance">)
      : undefined,
    selectablePlanIds: Array.isArray(billing.selectablePlanIds)
      ? billing.selectablePlanIds.filter((id): id is string => typeof id === "string")
      : undefined,
    intelligenceCapacityId: intelCapacity,
    intelligencePrice:
      typeof intel.price === "number" ? intel.price : null,
    intelligenceCreditsAllowance:
      typeof intel.creditsAllowance === "number" ? intel.creditsAllowance : null,
    intelligenceCreditsUsed:
      typeof intel.creditsUsed === "number" ? intel.creditsUsed : null,
    intelligenceTrialing: Boolean(intel.trialing),
    intelligencePeriodEnd: mapTimestamp(intel.periodEnd),
    hasUsedIntelligenceTrial:
      typeof billing.hasUsedIntelligenceTrial === "boolean"
        ? billing.hasUsedIntelligenceTrial
        : undefined,
    stripeCustomerId:
      typeof billing.stripeCustomerId === "string"
        ? billing.stripeCustomerId
        : undefined,
    stripeSubscriptionId:
      typeof billing.stripeSubscriptionId === "string"
        ? billing.stripeSubscriptionId
        : undefined,
    stripePriceId:
      typeof billing.stripePriceId === "string"
        ? billing.stripePriceId
        : undefined,
    hasUsedFreeTrial:
      typeof billing.hasUsedFreeTrial === "boolean"
        ? billing.hasUsedFreeTrial
        : undefined,
    created: mapTimestamp(data.created),
    updated: mapTimestamp(data.updated),
  };
}

const getBackendBaseUrl = (): string => {
  const raw = process.env.REACT_APP_BACKEND_URL;
  if (!raw) {
    throw new Error("Missing REACT_APP_BACKEND_URL environment variable.");
  }
  return raw.replace(/\/+$/, "");
};

export interface OrganizationsPageResult {
  organizations: OrganizationRecord[];
  lastDoc: DocumentSnapshot | null;
  hasMore: boolean;
}

/**
 * Fetch one page of organizations (ordered by created desc).
 * @param startAfterDoc - Pass the previous page's lastDoc, or null for the first page.
 * @param pageSize - Number of orgs per page (default 20).
 */
export const getOrganizationsPage = async (
  startAfterDoc: DocumentSnapshot | null,
  pageSize: number = DEFAULT_PAGE_SIZE
): Promise<OrganizationsPageResult> => {
  const ref = collection(firestore, ORGANIZATIONS_COLLECTION);
  const q = startAfterDoc
    ? query(
        ref,
        orderBy("created", "desc"),
        startAfter(startAfterDoc),
        limit(pageSize + 1)
      )
    : query(ref, orderBy("created", "desc"), limit(pageSize + 1));
  const snapshot = await getDocs(q);
  const docs = snapshot.docs;
  const hasMore = docs.length > pageSize;
  const pageDocs = hasMore ? docs.slice(0, pageSize) : docs;
  const lastDoc = pageDocs.length > 0 ? pageDocs[pageDocs.length - 1] : null;
  const organizations = pageDocs.map((d) =>
    mapRecord(d.id, d.data() as Record<string, unknown>)
  );
  return { organizations, lastDoc, hasMore };
};

/**
 * Fetch all organizations in pages (for export). Calls getOrganizationsPage in a loop.
 */
export const fetchAllOrganizations = async (
  pageSize: number = 100
): Promise<OrganizationRecord[]> => {
  const all: OrganizationRecord[] = [];
  let lastDoc: DocumentSnapshot | null = null;
  for (;;) {
    const result: OrganizationsPageResult = await getOrganizationsPage(lastDoc, pageSize);
    all.push(...result.organizations);
    if (!result.hasMore || !result.lastDoc) break;
    lastDoc = result.lastDoc;
  }
  return all;
};

export const PLAN_LIMITS: Record<PlanId, PlanLimits> = {
  micro: {
    id: "micro",
    name: "Micro",
    monthlyPrice: 11.99,
    currency: "USD",
    billingType: "fixed",
    maxSalesPeople: 3,
    maxCustomers: 120,
    maxInventoryItems: 250,
    maxSuppliers: 50,
    maxAdmins: 1,
  },
  standard: {
    id: "standard",
    name: "Standard",
    monthlyPrice: 29.99,
    currency: "USD",
    billingType: "fixed",
    maxSalesPeople: 20,
    maxCustomers: 1000,
    maxInventoryItems: 2500,
    maxSuppliers: 300,
    maxAdmins: 5,
  },
  professional: {
    id: "professional",
    name: "Professional",
    monthlyPrice: 59.99,
    currency: "USD",
    billingType: "fixed",
    maxSalesPeople: 50,
    maxCustomers: 5000,
    maxInventoryItems: 10000,
    maxSuppliers: 1500,
    maxAdmins: 10,
  },
  enterprise: {
    id: "enterprise",
    name: "Enterprise",
    monthlyPrice: null,
    currency: "USD",
    billingType: "quote",
    maxSalesPeople: 1000000,
    maxCustomers: 1000000,
    maxInventoryItems: 1000000,
    maxSuppliers: 1000000,
    maxAdmins: 1000000,
  },
  custom: {
    id: "custom",
    name: "Custom",
    monthlyPrice: null,
    currency: "USD",
    billingType: "fixed",
    maxSalesPeople: 0,
    maxCustomers: 0,
    maxInventoryItems: 0,
    maxSuppliers: 0,
    maxAdmins: 0,
  },
};

export const getPlanLimits = (planId: PlanId): PlanLimits =>
  PLAN_LIMITS[planId] ?? PLAN_LIMITS.micro;

export interface UpdateBillingParams {
  subscriptionStatus?: SubscriptionStatus;
  planId?: PlanId;
  customPlan?: Partial<PlanLimits>;
  stripeCustomerId?: string | null;
  stripeSubscriptionId?: string | null;
  stripePriceId?: string | null;
  paymentMethodsAllowed?: Array<"ves" | "stripe" | "binance"> | null;
  /** Plan IDs this org can choose from in the Paywall (e.g. when trialing). Empty/undefined = all fixed plans. */
  selectablePlanIds?: string[] | null;
  currentPeriodEnd?: Date;
  hasUsedFreeTrial?: boolean;
}

/**
 * Update organization billing and/or plan. Use for activating, deactivating, changing plan.
 */
export const updateOrganizationBilling = async (
  organizationId: string,
  params: UpdateBillingParams
): Promise<void> => {
  const orgRef = doc(firestore, ORGANIZATIONS_COLLECTION, organizationId);
  const now = Timestamp.now();

  const updates: Record<string, unknown> = {
    updated: now,
  };

  if (params.subscriptionStatus !== undefined) {
    updates["billing.subscriptionStatus"] = params.subscriptionStatus;
  }
  if (params.planId !== undefined) {
    updates["billing.planId"] = params.planId;
  }
  if (params.planId !== undefined || params.customPlan !== undefined) {
    if (params.planId === "custom") {
      updates["plan"] = params.customPlan ?? getPlanLimits("custom");
    } else {
      const basePlan = params.planId ? getPlanLimits(params.planId) : null;
      updates["plan"] = {
        ...(basePlan ?? {}),
        ...(params.customPlan ?? {}),
      };
    }
  }
  if (params.currentPeriodEnd !== undefined) {
    updates["billing.currentPeriodEnd"] = Timestamp.fromDate(
      params.currentPeriodEnd
    );
  }
  if (params.hasUsedFreeTrial !== undefined) {
    updates["billing.hasUsedFreeTrial"] = params.hasUsedFreeTrial;
  }
  if (params.stripeCustomerId !== undefined) {
    updates["billing.stripeCustomerId"] = params.stripeCustomerId;
  }
  if (params.stripeSubscriptionId !== undefined) {
    updates["billing.stripeSubscriptionId"] = params.stripeSubscriptionId;
  }
  if (params.stripePriceId !== undefined) {
    updates["billing.stripePriceId"] = params.stripePriceId;
  }
  if (params.paymentMethodsAllowed !== undefined) {
    updates["billing.paymentMethodsAllowed"] = params.paymentMethodsAllowed;
  }
  if (params.selectablePlanIds !== undefined) {
    updates["billing.selectablePlanIds"] = params.selectablePlanIds;
  }

  await updateDoc(orgRef, updates as unknown as Record<string, object>);
};

export const createStripePriceForCustomPlan = async (
  user: User,
  organizationId: string,
  planName: string,
  monthlyPrice: number,
  currency: string = "USD"
): Promise<{ stripeProductId: string; stripePriceId: string }> => {
  const token = await user.getIdToken();
  const baseUrl = getBackendBaseUrl();
  const response = await fetch(`${baseUrl}/api/v1/organizations/create-stripe-price`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      organizationId,
      planName,
      monthlyPrice,
      currency,
    }),
  });
  const data = (await response.json().catch(() => null)) as unknown;
  if (!response.ok) {
    const detail =
      data && typeof data === "object" && "detail" in data && typeof (data as { detail?: unknown }).detail === "string"
        ? (data as { detail: string }).detail
        : `Backend error (${response.status}).`;
    throw new Error(detail);
  }
  return data as { stripeProductId: string; stripePriceId: string };
};

/**
 * Set organization active flag (enable/disable org).
 */
export const updateOrganizationActive = async (
  organizationId: string,
  active: boolean
): Promise<void> => {
  const orgRef = doc(firestore, ORGANIZATIONS_COLLECTION, organizationId);
  await updateDoc(orgRef, {
    active,
    updated: Timestamp.now(),
  });
};

/**
 * Start trial: set status to trialing and period end to now + 30 days.
 */
export const startTrial = async (organizationId: string): Promise<void> => {
  const periodEnd = new Date();
  periodEnd.setDate(periodEnd.getDate() + 30);
  await updateOrganizationBilling(organizationId, {
    subscriptionStatus: "trialing",
    planId: "micro",
    currentPeriodEnd: periodEnd,
    hasUsedFreeTrial: true,
  });
};

/** Contact info for a user in an organization (from users collection). */
export interface OrgContact {
  displayName?: string;
  phone?: string;
  email: string;
}

/**
 * Fetch users that belong to the given organization IDs. Returns a map orgId -> contacts (admins/users).
 * Used for export to get contact name and phone per org.
 */
export const fetchContactsForOrganizations = async (
  orgIds: string[]
): Promise<Map<string, OrgContact[]>> => {
  if (orgIds.length === 0) return new Map();
  const ref = collection(firestore, USERS_COLLECTION);
  const snapshot = await getDocs(query(ref, limit(MAX_USERS)));
  const idSet = new Set(orgIds);
  const map = new Map<string, OrgContact[]>();
  snapshot.docs.forEach((docSnap) => {
    const d = docSnap.data() as Record<string, unknown>;
    const orgIdsUser = (d.organizationIds as string[] | undefined) ?? [];
    const contact: OrgContact = {
      displayName:
        typeof d.displayName === "string" ? d.displayName : undefined,
      phone: typeof d.phone === "string" ? d.phone : undefined,
      email: typeof d.email === "string" ? d.email : "",
    };
    orgIdsUser.forEach((oid) => {
      if (idSet.has(oid)) {
        const list = map.get(oid) ?? [];
        list.push(contact);
        map.set(oid, list);
      }
    });
  });
  return map;
};

export interface OrganizationDeletionPreview {
  organizationId: string;
  name: string;
  isSandbox: boolean;
  sandboxParentOrganizationId?: string | null;
  linkedSandboxOrganizationId?: string | null;
  subscriptionStatus?: string | null;
  hasStripeSubscription: boolean;
  customDomain?: string | null;
  userCount: number;
  blockers: string[];
  warnings: string[];
}

export interface OrganizationDeletionResult {
  organizationId: string;
  deletedLinkedSandboxId?: string | null;
  usersUpdated: number;
  usersDeleted: number;
  stripeSubscriptionCanceled: boolean;
  customDomainRemoved: boolean;
  storageObjectsDeleted: number;
  warnings: string[];
}

export const fetchOrganizationDeletionPreview = async (
  user: User,
  organizationId: string,
): Promise<OrganizationDeletionPreview> => {
  const token = await user.getIdToken();
  const baseUrl = getBackendBaseUrl();
  const response = await fetch(
    `${baseUrl}/api/v1/platform/organizations/${encodeURIComponent(organizationId)}/deletion-preview`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  const data = (await response.json().catch(() => null)) as unknown;
  if (!response.ok) {
    const detail =
      data && typeof data === "object" && "detail" in data
        ? String((data as { detail: unknown }).detail)
        : `Backend error (${response.status}).`;
    throw new Error(detail);
  }
  return data as OrganizationDeletionPreview;
};

export const deleteOrganization = async (
  user: User,
  organizationId: string,
  params: { confirmName: string; deleteLinkedSandbox?: boolean },
): Promise<OrganizationDeletionResult> => {
  const token = await user.getIdToken();
  const baseUrl = getBackendBaseUrl();
  const response = await fetch(
    `${baseUrl}/api/v1/platform/organizations/${encodeURIComponent(organizationId)}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        confirmName: params.confirmName,
        deleteLinkedSandbox: params.deleteLinkedSandbox ?? false,
      }),
    },
  );
  const data = (await response.json().catch(() => null)) as unknown;
  if (!response.ok) {
    const detail =
      data && typeof data === "object" && "detail" in data
        ? String((data as { detail: unknown }).detail)
        : `Backend error (${response.status}).`;
    throw new Error(detail);
  }
  return data as OrganizationDeletionResult;
};

export const grantOrganizationIntelligence = async (
  user: User,
  organizationId: string,
  params: {
    capacityId: "lite" | "plus" | "extra" | null;
    creditsAllowance?: number | null;
    price?: number | null;
    currency?: string;
    resetCreditsUsed?: boolean;
    trialing?: boolean;
    trialDays?: number;
  },
): Promise<unknown> => {
  const token = await user.getIdToken();
  const baseUrl = getBackendBaseUrl();
  const response = await fetch(
    `${baseUrl}/api/v1/platform/organizations/${organizationId}/intelligence`,
    {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        capacityId: params.capacityId,
        creditsAllowance: params.creditsAllowance ?? undefined,
        price: params.price ?? undefined,
        currency: params.currency,
        resetCreditsUsed: params.resetCreditsUsed ?? true,
        trialing: params.trialing ?? false,
        trialDays: params.trialDays,
      }),
    },
  );
  const data = (await response.json().catch(() => null)) as unknown;
  if (!response.ok) {
    const detail =
      data && typeof data === "object" && "detail" in data
        ? String((data as { detail: unknown }).detail)
        : `Backend error (${response.status}).`;
    throw new Error(detail);
  }
  return data;
};

export interface IntelligenceTrialResult {
  capacityId?: string | null;
  trialing?: boolean;
  periodEnd?: string | null;
  creditsAllowance?: number;
  creditsUsed?: number;
}

export const startOrganizationIntelligenceTrial = async (
  user: User,
  organizationId: string,
  params?: {
    capacityId?: "lite" | "plus" | "extra";
    trialDays?: number;
    force?: boolean;
  },
): Promise<IntelligenceTrialResult> => {
  const token = await user.getIdToken();
  const baseUrl = getBackendBaseUrl();
  const response = await fetch(
    `${baseUrl}/api/v1/platform/organizations/${encodeURIComponent(organizationId)}/intelligence/trial`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        capacityId: params?.capacityId,
        trialDays: params?.trialDays,
        force: params?.force ?? true,
      }),
    },
  );
  const data = (await response.json().catch(() => null)) as unknown;
  if (!response.ok) {
    const detail =
      data && typeof data === "object" && "detail" in data
        ? String((data as { detail: unknown }).detail)
        : `Backend error (${response.status}).`;
    throw new Error(detail);
  }
  return (data && typeof data === "object" ? data : {}) as IntelligenceTrialResult;
};
